/**
 * One row shape for every statement format.
 *
 * Bank exports (George CSV / XLSX / JSON, other banks' CSV) and card statements (PDF) describe
 * the same card charge differently: other dates, other text. Each reader below turns its format
 * into `StatementRow`s, and `reconcile.ts` decides which of them are already booked. Nothing here
 * talks to PocketBase or the network.
 */

/**
 * "export": a bank export, dated with the day the charge was posted to the account.
 * "statement": the card company's monthly statement, dated with the day the card was used.
 */
export type SourceFamily = "export" | "statement";

export type StatementRow = {
  family: SourceFamily;
  /** The date this source identifies the row by: posting date (export) or purchase date (statement). */
  date: string;
  purchaseDate?: string;
  cardBookingDate?: string;
  postingDate?: string;
  valueDate?: string;
  /** Signed, in cents: negative is a charge. Cents, because this is what gets compared. */
  amountCents: number;
  currency: string;
  /** What two formats have in common: the merchant without FX details or booking noise. */
  merchant: string;
  counterparty: string;
  /** The text as the source printed it. */
  description: string;
  reference: string;
  location: string;
  foreignAmount?: number;
  foreignCurrency?: string;
  exchangeRate?: number;
  fee?: number;
};

export type StatementFormat = "csv" | "xlsx" | "json" | "pdf";

/** By content first, file name second: a renamed export is still the format it contains. */
export function detectFormat(fileName: string, head: Uint8Array): StatementFormat {
  if (head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46) return "pdf"; // %PDF
  if (head[0] === 0x50 && head[1] === 0x4b) return "xlsx"; // PK - a zip, which is what xlsx is
  const name = fileName.toLowerCase();
  if (name.endsWith(".pdf")) return "pdf";
  if (name.endsWith(".xlsx")) return "xlsx";
  if (name.endsWith(".json")) return "json";
  // UTF-8 or UTF-16 JSON: the first visible character is a bracket.
  const firstVisible = [...head].find(
    (byte) =>
      byte > 0x20 &&
      byte !== 0xff &&
      byte !== 0xfe &&
      byte !== 0xef &&
      byte !== 0xbb &&
      byte !== 0xbf,
  );
  if (firstVisible === 0x5b || firstVisible === 0x7b) return "json";
  return "csv";
}

/** Last four card digits from a masked number such as "490473XXXXXX2426" in a file name or header. */
export function cardLast4(text: string | undefined): string | undefined {
  return /\d{4,6}[X*]{4,10}(\d{4})/i.exec(text ?? "")?.[1];
}

const FX_TAIL = /\s+inkl\.?\s+Fremdw(?:ä|ae)hrungsentgelt\b.*$/i;
const FX_DETAILS = /Fremdw(?:ä|ae)hrungsentgelt\s+(\d+(?:[.,]\d+)?)\s+Kurs\s+(\d+(?:[.,]\d+)?)/i;

/** "BASE44 inkl. Fremdwährungsentgelt 0,65 Kurs 1,1526048" -> "BASE44". */
export function merchantFromDescription(description: string): string {
  return description.replace(FX_TAIL, "").trim();
}

/** Fee and exchange rate George writes into the booking text of a foreign-currency charge. */
export function fxFromDescription(description: string): { fee?: number; exchangeRate?: number } {
  const match = FX_DETAILS.exec(description);
  if (!match) return {};
  return {
    fee: Number(match[1]!.replace(",", ".")),
    exchangeRate: Number(match[2]!.replace(",", ".")),
  };
}

/**
 * Paying off the card ("Ihre Zahlung Vormonat") is a transfer from the business account, not an
 * expense, so it never belongs on the card's list of charges.
 */
const SETTLEMENT =
  /\b(ihre zahlung|zahlung vormonat|payment received|thank you for your payment)\b/i;

export function isCardSettlement(
  row: Pick<StatementRow, "amountCents" | "description" | "merchant">,
): boolean {
  return row.amountCents > 0 && (SETTLEMENT.test(row.description) || SETTLEMENT.test(row.merchant));
}

export const toCents = (amount: number) => Math.round(amount * 100);

/** The George JSON export: an array of bookings with the amount as integer + precision. */
type GeorgeBooking = {
  booking?: string | null;
  valuation?: string | null;
  amount?: { value?: number | null; precision?: number | null; currency?: string | null } | null;
  reference?: string | null;
  partnerName?: string | null;
  merchantName?: string | null;
  referenceNumber?: string | null;
};

export function rowsFromGeorgeJson(json: unknown): StatementRow[] {
  const list = Array.isArray(json)
    ? json
    : Array.isArray((json as { transactions?: unknown })?.transactions)
      ? (json as { transactions: unknown[] }).transactions
      : null;
  if (!list) throw new Error("unsupported_json");

  const rows: StatementRow[] = [];
  for (const entry of list as GeorgeBooking[]) {
    const date = entry?.booking?.slice(0, 10);
    const value = entry?.amount?.value;
    if (!date || typeof value !== "number") continue;
    const precision = entry.amount?.precision ?? 2;
    const description = (entry.reference ?? "").trim();
    const counterparty = (entry.partnerName ?? entry.merchantName ?? "").trim();
    rows.push({
      family: "export",
      date,
      postingDate: date,
      valueDate: entry.valuation?.slice(0, 10) || undefined,
      amountCents: Math.round(value * 10 ** (2 - precision)),
      currency: entry.amount?.currency ?? "EUR",
      merchant: counterparty || merchantFromDescription(description),
      counterparty,
      description,
      reference: (entry.referenceNumber ?? "").trim(),
      location: "",
      ...fxFromDescription(description),
    });
  }
  return rows;
}

/** What POST /ai/statement returns for a card statement PDF. */
export type ParsedCardStatement = {
  cardNumber?: string;
  statementNumber?: string;
  statementDate?: string;
  rows: Array<{
    purchaseDate: string;
    bookingDate: string;
    merchant: string;
    location: string;
    foreignAmount?: number;
    foreignCurrency?: string;
    exchangeRate?: number;
    fee?: number;
    amount: number;
    text: string;
  }>;
};

export function rowsFromCardStatement(statement: ParsedCardStatement): StatementRow[] {
  return statement.rows.map((row) => ({
    family: "statement",
    date: row.purchaseDate,
    purchaseDate: row.purchaseDate,
    cardBookingDate: row.bookingDate,
    amountCents: toCents(row.amount),
    currency: "EUR",
    merchant: row.merchant,
    counterparty: row.merchant,
    description: row.text,
    reference: "",
    location: row.location,
    foreignAmount: row.foreignAmount,
    foreignCurrency: row.foreignCurrency,
    exchangeRate: row.exchangeRate,
    fee: row.fee,
  }));
}

/**
 * An XLSX sheet as the same string table a CSV becomes, so both share one column mapping.
 * Dates arrive as midnight-UTC `Date`s and amounts as numbers.
 */
export function xlsxCellsToTable(rows: unknown[][]): string[][] {
  return rows.map((row) =>
    row.map((cell) => {
      if (cell === null || cell === undefined) return "";
      if (cell instanceof Date) return cell.toISOString().slice(0, 10);
      return String(cell);
    }),
  );
}
